// billing-modals.js — Promise-based modal helpers for the billing flow.
//
// Two modals live here:
//
//   showCancellationSurvey()
//     Optional follow-up after the user confirms cancelling their
//     subscription. Asks "why?" via radio buttons + free-form details.
//     Resolves with { code, details } if submitted, or null if the
//     user backed out. Submission is best-effort — the actual
//     cancellation goes through regardless of survey result.
//
//   showBillingChangeConfirm(opts)
//     Pre-flight confirmation for any destructive billing action
//     (cancel, paid-paid downgrade). Renders different copy per
//     scenario (cancel vs downgrade vs upgrade-fallback) so users
//     understand exactly what they're agreeing to, when it takes
//     effect, and what they keep / lose.
//
// Both rely on static DOM scaffolding in index.html (#cancel-survey-*
// and #billing-change-* backdrop + body + button ids). They wire
// listeners on each open and tear them down on close so calls
// don't leak event listeners across re-opens.
//
// Hoisted from src/bootstrap.js on 2026-05-13 (Phase K-a). Pure
// DOM ops, no module-level state, no supabase coupling.

// showCancellationSurvey: presents a "why are you leaving?" survey
// with five radio options + an optional details textarea. Returns
// the picked reason on submit, or null if the user clicks Back /
// backdrop (treat that as "they changed their mind — abort the
// whole cancel flow").
export function showCancellationSurvey() {
  return new Promise((resolve) => {
    const backdrop = document.getElementById("cancel-survey-backdrop");
    const details = document.getElementById("cancel-reason-details");
    const radios = document.querySelectorAll('input[name="cancel-reason"]');
    radios.forEach((r) => {
      r.checked = false;
    });
    details.value = "";
    backdrop.classList.add("open");
    const submit = document.getElementById("cancel-survey-submit");
    const back = document.getElementById("cancel-survey-back");
    const cleanup = () => {
      backdrop.classList.remove("open");
      submit.removeEventListener("click", onSubmit);
      back.removeEventListener("click", onBack);
      backdrop.removeEventListener("click", onBackdrop);
    };
    const onSubmit = () => {
      const picked = document.querySelector(
        'input[name="cancel-reason"]:checked',
      );
      const code = picked ? picked.value : "";
      cleanup();
      resolve({ code, details: details.value.trim() });
    };
    const onBack = () => {
      cleanup();
      resolve(null);
    };
    const onBackdrop = (e) => {
      if (e.target === backdrop) {
        cleanup();
        resolve(null);
      }
    };
    submit.addEventListener("click", onSubmit);
    back.addEventListener("click", onBack);
    backdrop.addEventListener("click", onBackdrop);
  });
}

/**
 * Billing-change confirmation modal. Shown before any destructive billing
 * action (cancel, or downgrade between paid tiers) so the user sees:
 *   - Which plan they're moving to
 *   - When the change takes effect (period end for cancel, immediately
 *     for paid→paid downgrade, with prorated credit mentioned)
 *
 * @param {Object} opts
 *   currentTier    — 'startup' | 'enterprise'
 *   targetTier     — 'solo' | 'startup' | 'enterprise'
 *   periodEnd      — ISO string (billing.current_period_end)
 *   confirmLabel   — button text, e.g. "Cancel subscription" or "Downgrade"
 *   danger         — boolean; uses danger styling on confirm button
 * Returns Promise<boolean> — true if user confirmed, false if backed out.
 */
export function showBillingChangeConfirm(opts) {
  const { currentTier, targetTier, periodEnd, confirmLabel, danger } = opts;
  return new Promise((resolve) => {
    const backdrop = document.getElementById("billing-change-backdrop");
    const title = document.getElementById("billing-change-title");
    const body = document.getElementById("billing-change-body");
    const submit = document.getElementById("billing-change-submit");
    const cancel = document.getElementById("billing-change-cancel");

    // Tier IDs stay as 'solo'/'startup'/'enterprise' in the DB; the
    // public-facing names are Free/Team/Enterprise (2026-05-15).
    const tierName = (t) =>
      t === "enterprise" ? "Enterprise" : t === "startup" ? "Team" : "Free";
    const fmtDate = (iso) => {
      try {
        return new Date(iso).toLocaleDateString(undefined, {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      } catch {
        return "the end of your billing period";
      }
    };

    // Render copy per scenario.
    if (targetTier === "solo") {
      // Cancel → drop to free at period end.
      title.textContent = "Cancel your subscription?";
      body.innerHTML =
        "You'll keep <strong>" +
        tierName(currentTier) +
        "</strong> features until " +
        "<strong>" +
        fmtDate(periodEnd) +
        "</strong>, then move to the <strong>Free</strong> tier automatically." +
        "<br><br>" +
        "You won't be charged again. Team data, members, and audit history are preserved &mdash; you can upgrade again anytime.";
    } else if (currentTier === "enterprise" && targetTier === "startup") {
      // Paid downgrade — immediate with proration.
      title.textContent = "Downgrade to " + tierName(targetTier) + "?";
      body.innerHTML =
        "You'll move from <strong>" +
        tierName(currentTier) +
        "</strong> to <strong>" +
        tierName(targetTier) +
        "</strong> " +
        "<strong>today</strong>. A prorated credit for the unused portion of your " +
        tierName(currentTier) +
        " plan " +
        "will be applied to your next invoice." +
        "<br><br>" +
        tierName(currentTier) +
        "-only features (like team analytics and SSO) will no longer be available.";
    } else if (currentTier === "startup" && targetTier === "enterprise") {
      // Paid upgrade — immediate, prorated charge. Not typically shown
      // (we skip this confirm on upgrades) but included for completeness.
      title.textContent = "Upgrade to " + tierName(targetTier) + "?";
      body.innerHTML =
        "You'll upgrade from <strong>" +
        tierName(currentTier) +
        "</strong> to <strong>" +
        tierName(targetTier) +
        "</strong> " +
        "<strong>today</strong>. You'll be charged a prorated amount for the rest of this billing period, " +
        "then the full " +
        tierName(targetTier) +
        " price on every renewal.";
    } else {
      // Fallback — same-tier or unexpected combination.
      title.textContent = "Confirm plan change";
      body.innerHTML =
        "You're moving from <strong>" +
        tierName(currentTier) +
        "</strong> to <strong>" +
        tierName(targetTier) +
        "</strong>.";
    }

    submit.textContent = confirmLabel || "Confirm";
    submit.className = danger ? "btn-confirm-danger" : "btn-confirm-primary";

    backdrop.classList.add("open");
    const cleanup = () => {
      backdrop.classList.remove("open");
      submit.removeEventListener("click", onOK);
      cancel.removeEventListener("click", onCancel);
      backdrop.removeEventListener("click", onBackdrop);
    };
    const onOK = () => {
      cleanup();
      resolve(true);
    };
    const onCancel = () => {
      cleanup();
      resolve(false);
    };
    const onBackdrop = (e) => {
      if (e.target === backdrop) {
        cleanup();
        resolve(false);
      }
    };
    submit.addEventListener("click", onOK);
    cancel.addEventListener("click", onCancel);
    backdrop.addEventListener("click", onBackdrop);
  });
}
